import React, { useState, useCallback } from "react";
import {
  makeStyles,
  tokens,
  Card,
  Text,
  Input,
  Field,
  Textarea,
  Button,
  Radio,
  RadioGroup,
  MessageBar,
  Spinner,
} from "@fluentui/react-components";
import { SearchRegular, CheckmarkRegular } from "@fluentui/react-icons";
import { useMsal, useAccount } from "@azure/msal-react";
import { ApiError, useApiClient } from "../hooks/useApiClient";
import { OrderData, CFROIResponse } from "../types/api";
import OrderInformation from "../components/OrderInformation";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXL,
  },
  searchSection: {
    padding: tokens.spacingVerticalL,
  },
  searchInput: {
    maxWidth: "400px",
    marginBottom: tokens.spacingVerticalM,
  },
  approvalSection: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    justifyContent: "flex-end",
    padding: tokens.spacingVerticalL,
  },
  searchButton: {
    // Will now use the brand colors from the theme automatically
  },
  approvalButton: {
    minWidth: "200px",
    // Will now use the brand colors from the theme automatically
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: tokens.spacingVerticalXXL,
  },
  lookupSection: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    paddingTop: tokens.spacingVerticalM,
  },
});

// Default shipping times in days for each order type
const SHIPPING_TIME_PRESETS = {
  FOB: 90,
  Distribution: 5,
};

const PurchaseApprovalPage: React.FC = () => {
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [orderNumber, setOrderNumber] = useState("");
  const [requestNotes, setRequestNotes] = useState("");
  const [orderType, setOrderType] = useState<"FOB" | "Distribution">("FOB");
  const [paymentTerms, setPaymentTerms] = useState(30);
  const [shippingTime, setShippingTime] = useState(SHIPPING_TIME_PRESETS.FOB);
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [cfroiData, setCfroiData] = useState<CFROIResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);

  // Update shipping time when order type changes
  const handleOrderTypeChange = (value: "FOB" | "Distribution") => {
    setOrderType(value);
    setShippingTime(SHIPPING_TIME_PRESETS[value]);
  };

  const apiClient = useApiClient();
  const styles = useStyles();

  const handleSearch = useCallback(async () => {
    if (!orderNumber.trim()) {
      setError("Please enter an order number");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch CFROI data which includes all the order information plus payment series
      // Pass order type, payment terms, and shipping time for accurate calculations
      const cfroiResponse = await apiClient.getCFROIData(orderNumber.trim(), {
        orderType: orderType,
        paymentTerms: paymentTerms,
        shippingTime: shippingTime,
      });

      // Transform CFROI data to OrderData format
      if (cfroiResponse.skuPaymentSeries) {
        const transformedOrderData: OrderData = {
          supplier: cfroiResponse.supplier,
          erpPaymentTerms: cfroiResponse.erpPaymentTerms,
          orderNumber: cfroiResponse.orderNumber, // Use orderNumber instead of requestId
          purchasingEntity: "Caseking", // These fields might need to be added to CFROIResponse
          purchasingManager: cfroiResponse.purchasingManager || "TBD", // or retrieved from the API differently
          paymentTerms: paymentTerms, // Use the user-selected payment terms
          cfroi: cfroiResponse.portfolioCfroi || 0,
          skus: cfroiResponse.skuPaymentSeries.map((skuData) => ({
            skuId: skuData.position.sku,
            itemName: skuData.position.itemName,
            purchasingVolume:
              skuData.position.price * skuData.position.quantity,
            amount: skuData.position.quantity,
            currentStock: skuData.currentStock,
            cfroi: skuData.cfroi,
            npv: skuData.npv,
          })),
        };

        setOrderData(transformedOrderData);
        setCfroiData(cfroiResponse);
      } else {
        throw new Error("No SKU payment series data found");
      }
    } catch (err) {
      setError(
        "Failed to load order data. Please check the order number and try again."
      );
      setOrderData(null);
      setCfroiData(null);
    } finally {
      setLoading(false);
    }
  }, [orderNumber, apiClient, orderType, paymentTerms, shippingTime]);

  const handleApproval = useCallback(async () => {
    if (!orderData || !cfroiData || !account) return;

    setApproving(true);
    setError(null);

    try {
      // Submit approval request with required fields
      await apiClient.requestApproval({
        requestNotes: requestNotes.trim(),
        orderNumber: orderData.orderNumber,
        orderType, // Required - FOB or Distribution
        paymentTerms, // Required - payment terms in days
        shippingTime, // Save the requester's chosen shipping time
        expirationDays: 7, // Default to 7 days
      });

      // Show success message or redirect
      alert("Approval request submitted successfully!");
      setRequestNotes("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to submit approval request. Please try again.");
    } finally {
      setApproving(false);
    }
  }, [orderData, cfroiData, orderType, paymentTerms, shippingTime, requestNotes, account, apiClient]);

  return (
    <div className={styles.container}>
      {/* Search Section */}
      <Card className={styles.searchSection}>
        <Text size={500} weight="semibold">
          Order Lookup
        </Text>
        <div className={styles.lookupSection}>
          <Input
            className={styles.searchInput}
            placeholder="Enter order number..."
            value={orderNumber}
            onChange={(e) => { setOrderNumber(e.target.value); setRequestNotes(""); }}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            autoFocus
          />
          <Button
            appearance="primary"
            icon={<SearchRegular />}
            onClick={handleSearch}
            disabled={loading}
            className={styles.searchButton}
            style={{ marginLeft: tokens.spacingHorizontalM }}
          >
            Search
          </Button>
        </div>

        {/* Order Configuration */}
        <div
          style={{
            marginTop: tokens.spacingVerticalM,
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: tokens.spacingHorizontalL,
            maxWidth: "700px",
          }}
        >
          <div>
            <Text
              size={300}
              weight="semibold"
              style={{
                marginBottom: tokens.spacingVerticalXS,
                display: "block",
              }}
            >
              Order Type
            </Text>
            <RadioGroup
              value={orderType}
              onChange={(_, data) => handleOrderTypeChange(data.value as "FOB" | "Distribution")}
              layout="horizontal"
            >
              <Radio value="FOB" label="FOB" />
              <Radio value="Distribution" label="Distribution" />
            </RadioGroup>
          </div>

          <div>
            <Text
              size={300}
              weight="semibold"
              style={{
                marginBottom: tokens.spacingVerticalXS,
                display: "block",
              }}
            >
              Payment Terms (days)
            </Text>
            <Input
              type="number"
              value={paymentTerms.toString()}
              onChange={(e) => setPaymentTerms(parseInt(e.target.value) || 0)}
              style={{ maxWidth: "120px" }}
            />
          </div>

          <div>
            <Text
              size={300}
              weight="semibold"
              style={{
                marginBottom: tokens.spacingVerticalXS,
                display: "block",
              }}
            >
              Shipping Time (days)
            </Text>
            <Input
              type="number"
              value={shippingTime.toString()}
              onChange={(e) => setShippingTime(parseInt(e.target.value) || 0)}
              style={{ maxWidth: "120px" }}
            />
          </div>
        </div>
      </Card>

      {/* Error Message */}
      {error && <MessageBar intent="error">{error}</MessageBar>}

      {/* Loading */}
      {loading && (
        <div className={styles.loading}>
          <Spinner size="large" label="Loading order data..." />
        </div>
      )}

      {/* Order Information */}
      {orderData && (
        <>
          <OrderInformation
            orderData={orderData}
            cfroiData={cfroiData || undefined}
            paymentTerms={paymentTerms}
            showSkuInteraction={true}
            showCharts={true}
          />

          {/* Approval Section */}
          <Card className={styles.approvalSection}>
            <Field label="Notes for the approver (optional)" hint={`${requestNotes.length}/4000 characters`}>
              <Textarea
                value={requestNotes}
                onChange={(_, data) => setRequestNotes(data.value)}
                maxLength={4000}
                rows={4}
                disabled={approving}
                placeholder="Explain the purchase or add context for the approval decision."
              />
            </Field>
            <Button
              appearance="primary"
              size="large"
              icon={<CheckmarkRegular />}
              className={styles.approvalButton}
              onClick={handleApproval}
              disabled={approving || !cfroiData || !cfroiData.skuPaymentSeries}
            >
              {approving ? "Requesting approval..." : "Request approval"}
            </Button>
            {!cfroiData && (
              <Text>
                Please calculate CFROI first before requesting approval.
              </Text>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default PurchaseApprovalPage;
